import type { ProcessImageResult } from './assetTypes'

export interface ImageProcessor {
  process(assetId: string, blob: Blob, mimeType: string): Promise<ProcessImageResult>
  dispose?(): void
}

interface WorkerResponse {
  id: string
  result?: ProcessImageResult
  error?: string
}

interface PendingRequest {
  resolve: (result: ProcessImageResult) => void
  reject: (error: Error) => void
}

export class ImageWorkerProcessor implements ImageProcessor {
  private worker: Worker | null = null
  private readonly pending = new Map<string, PendingRequest>()

  process(assetId: string, blob: Blob, mimeType: string): Promise<ProcessImageResult> {
    const worker = this.getWorker()
    return new Promise((resolve, reject) => {
      this.pending.set(assetId, { resolve, reject })
      worker.postMessage({ id: assetId, blob, mimeType })
    })
  }

  dispose(): void {
    this.worker?.terminate()
    this.worker = null
    for (const request of this.pending.values()) {
      request.reject(new Error('Image processing stopped.'))
    }
    this.pending.clear()
  }

  private getWorker(): Worker {
    if (this.worker) return this.worker
    const worker = new Worker(new URL('./imageProcessor.worker.ts', import.meta.url), {
      type: 'module',
      name: 'minimany-image-processor',
    })
    worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
      const request = this.pending.get(event.data.id)
      if (!request) return
      this.pending.delete(event.data.id)
      if (event.data.result) request.resolve(event.data.result)
      else request.reject(new Error(event.data.error ?? 'Image processing failed.'))
    })
    worker.addEventListener('error', () => {
      for (const request of this.pending.values()) {
        request.reject(new Error('Image processing worker failed.'))
      }
      this.pending.clear()
      worker.terminate()
      if (this.worker === worker) this.worker = null
    })
    this.worker = worker
    return worker
  }
}

